import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { toErrMsg } from "@/lib/api";
import { Languages, ShieldCheck } from "lucide-react";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import { askAlert } from "@/lib/appDialog";

/* Batch 22 · PR B · items 43 / 44 (owner 14-Oct-2026): a lock (429) is a POP-UP in the app's dialog style with the minutes
   from the Retry-After header (a person who returns after 6 minutes sees "9 minutes"); a wrong password says how many
   attempts are left (X-Attempts-Left, for an existing account only); an unknown e-mail says so. Shared with VerifyLogin. */
export function lockMinutes(err) {
  const h = err?.response?.headers || {};
  const secs = parseInt(h["retry-after"] ?? h["Retry-After"] ?? "0", 10);
  return Math.max(1, Math.ceil((Number.isFinite(secs) ? secs : 0) / 60));
}
export async function showAuthError(err, t, fallback) {
  const status = err?.response?.status;
  const code = err?.response?.data?.error_code;
  if (status === 429) {
    const ipOnly = /from this address/i.test(String(err?.response?.data?.detail || ""));
    const msg = t(ipOnly ? "auth.lockedAddress" : "auth.lockedAccount").replace("{m}", String(lockMinutes(err)));
    await askAlert(msg, { title: t("auth.lockedTitle") });
    return;
  }
  if (code === "AUTH_PASSWORD_INCORRECT") {
    const left = err?.response?.headers?.["x-attempts-left"];
    toast.error(left != null && left !== "" ? t("auth.attemptsLeft").replace("{n}", String(left)) : toErrMsg(err, fallback));
    return;
  }
  toast.error(toErrMsg(err, fallback));
}

export default function Login() {
  const { user, login } = useAuth();
  const { t, lang, toggle } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    // Platform Admins land on /platform (Tenants tab is default there),
    // every other role lands on / (Dashboard).
    if (user) {
      const dest = user.role === "Platform Admin" ? "/platform" : "/";
      nav(dest, { replace: true });
    }
  }, [user, nav]);
  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.twofa_required) {
        nav("/login/verify", { state: {
          challenge_id: result.challenge_id,
          email_hint: result.email_hint,
          dev_code: result.dev_code,
          delivery_failed: result.delivery_failed,
          delivery_error: result.delivery_error,
          delivery_detail: result.delivery_detail,
          email, password,
        }});
        return;
      }
      // Non-2FA path: user context updates from login(); the useEffect
      // above will fire and route to the right landing page. Fallback
      // navigation kept for safety.
      nav("/");
    } catch (err) {
      await showAuthError(err, t, "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG5hdnklMjBnZW9tZXRyaWMlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NTMxMzc1OXww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="absolute inset-0 flex flex-col justify-end p-10 text-white">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-white/95 shadow-sm ring-1 ring-white/10">
              <img src="/azex-logo.png" alt="Azex" className="h-7 w-7 object-contain" />
            </div>
            <div className="text-lg font-semibold tracking-tight">{t("app.name")}</div>
          </div>
          <h1 className="text-3xl font-bold tracking-tight max-w-md">
            Planning to finalization. One workspace.
          </h1>
          <p className="mt-3 max-w-md text-sm text-slate-300">
            Standardize your GL, run automated analytics and sampling, generate vouching
            sheets, and cross-check every supporting document — governed, standards-based,
            audit-ready.
          </p>
        </div>
      </div>
      <div className="flex flex-col items-center justify-center bg-white dark:bg-slate-900 px-6 py-10">
        <div className="absolute top-4 end-4">
          <Button variant="ghost" size="sm" onClick={toggle} data-testid="lang-toggle-auth">
            <Languages className="me-1 h-4 w-4" />
            {lang === "en" ? "EN" : "ع"}
          </Button>
        </div>
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <h2 className="text-xl font-semibold">{t("auth.loginTitle")}</h2>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <button type="button" onClick={() => setForgotOpen(true)}
                        className="text-xs font-medium text-emerald-600 hover:underline"
                        data-testid="forgot-password-link">
                  Forgot password?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? t("common.loading") : t("auth.login")}
            </Button>
          </form>
          <p className="mt-4 text-xs text-muted-foreground text-center">{t("auth.tagline")}</p>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Accounts are created by your firm administrator. If you need access,
            please ask your Firm Admin or Platform Administrator.
          </p>
          <p className="mt-3 text-center text-xs">
            <Link to="/install" className="font-medium text-emerald-700 hover:underline" data-testid="login-install-link">{t("install.linkFromLogin")}</Link>
          </p>
        </div>
      </div>
      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
    </div>
  );
}
